import React, { useState, useEffect } from 'react';
import axios from 'axios';
import zipcodes from 'zipcodes';
import HOAOnboardingChecklist from './HOAOnboardingChecklist';
import AuthorizationAgreement from './components/AuthorizationAgreement';

const EnhancedHOASetupFlow = ({ onBack, onComplete, loadedQuoteData }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [createdHOAData, setCreatedHOAData] = useState(null);
  const [savedQuoteCode, setSavedQuoteCode] = useState('');
  
  // Authorization Agreement State
  const [authorizationData, setAuthorizationData] = useState({
    signatory_name: '',
    signatory_title: '',
    signatory_email: '',
    agreed_authority: false,
    agreed_terms: false,
    agreed_privacy: false,
    ip_address: '',
    timestamp: null
  });
  
  // Get user's IP address
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => {
        setAuthorizationData(prev => ({ ...prev, ip_address: data.ip }));
      })
      .catch(err => console.error('Failed to get IP:', err));
  }, []);

  // Load quote data when provided
  useEffect(() => {
    if (loadedQuoteData) {
      console.log('Loading quote data:', loadedQuoteData);
      if (loadedQuoteData.hoa_data) {
        setHOAData(prev => ({ ...prev, ...loadedQuoteData.hoa_data }));
      }
      if (loadedQuoteData.community_details) {
        setCommunityDetails(prev => ({ ...prev, ...loadedQuoteData.community_details }));
      }
      if (loadedQuoteData.board_setup) {
        setBoardSetup(prev => ({ ...prev, ...loadedQuoteData.board_setup }));
      }
      if (loadedQuoteData.operational_setup) {
        setOperationalSetup(prev => ({ ...prev, ...loadedQuoteData.operational_setup }));
      }
      if (loadedQuoteData.selected_plan) {
        setSelectedPlan(loadedQuoteData.selected_plan);
      }
      if (loadedQuoteData.selected_addons) {
        setSelectedAddOns(loadedQuoteData.selected_addons);
      }
      
      // Show success message
      setError('');
      alert(`✅ Quote loaded successfully! Your progress has been restored. Total: $${loadedQuoteData.total_amount}/year`);
    }
  }, [loadedQuoteData]);
  
  const [hoaData, setHOAData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    description: '',
    total_properties: '',
    website: '',
    contact_email: '',
    established_date: '',
    community_type: 'residential',
    management_company: '',
    current_management: 'self_managed'
  });

  const [communityDetails, setCommunityDetails] = useState({
    has_amenities: false,
    amenities: [],
    has_architectural_committee: false,
    has_landscape_committee: false,
    has_social_committee: false,
    has_fines_committee: false,
    meeting_frequency: 'monthly',
    fiscal_year_end: 'december',
    assessment_frequency: 'monthly',
    current_dues_amount: '',
    has_special_assessments: false,
    has_violations: false,
    maintenance_type: 'none' // none, entrance_only, common_areas
  });

  const [boardSetup, setBoardSetup] = useState({
    board_size: '5',
    positions: [
      { title: 'President', filled: false, name: '', email: '', term_length: '2', customTitle: '' },
      { title: 'Vice President', filled: false, name: '', email: '', term_length: '2', customTitle: '' },
      { title: 'Treasurer', filled: false, name: '', email: '', term_length: '2', customTitle: '' },
      { title: 'Secretary', filled: false, name: '', email: '', term_length: '2', customTitle: '' },
      { title: 'Member at Large', filled: false, name: '', email: '', term_length: '2', customTitle: '' }
    ],
    election_cycle: 'annual',
    next_election_date: '',
    term_limits: false,
    staggered_terms: false,
    require_homeowner: true
  });

  const [operationalSetup, setOperationalSetup] = useState({
    has_cc_rs: false,
    has_bylaws: false,
    has_rules_regulations: false,
    needs_document_help: false,
    has_reserve_study: false,
    reserve_fund_balance: '',
    operating_fund_balance: '',
    has_insurance: false,
    insurance_provider: '',
    state: '',
    incorporation_status: 'incorporated',
    attorney_contact: '',
    accountant_contact: '',
    needs_accounting_services: false,
    primary_communication: 'email',
    emergency_contact_method: 'email_phone',
    meeting_format: 'in_person'
  });

  const [selectedPlan, setSelectedPlan] = useState('standard');
  const [paymentFrequency, setPaymentFrequency] = useState('yearly'); // 'monthly' or 'yearly'
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [extraHomesQuantity, setExtraHomesQuantity] = useState(1); // integer count for extra homes
  const [extraStorageQuantity, setExtraStorageQuantity] = useState(1); // integer count for extra storage
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    name: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: ''
  });

  const pricingPlans = {
    basic: { 
      name: 'Platform Base', 
      homes: 'Up to 100 homes', 
      yearlyPrice: 399,
      monthlyPrice: 35,
      yearlyPriceId: 'price_1SMByyFuWSwki2tlunKmG3e3',
      monthlyPriceId: 'price_1SMByyFuWSwki2tlXOj2H9Xp',
      displayPrice: '$399/year ($33.25/mo)',
      period: '/year',
      features: [
        'Basic document storage (5GB)',
        'User management & board tools',
        'Payment processing for dues',
        'Community calendar',
        'Basic financial tracking'
      ]
    },
    standard: { 
      name: 'Platform Standard', 
      homes: 'Up to 300 homes', 
      yearlyPrice: 499,
      monthlyPrice: 45,
      yearlyPriceId: 'price_1SMCANFuWSwki2tljNvnIar0',
      monthlyPriceId: 'price_1SMCANFuWSwki2tle6Aybwu3',
      displayPrice: '$499/year ($41.58/mo)',
      period: '/year',
      popular: true,
      features: [
        'Everything in Basic',
        '✅ Budget & Finance Reporting (included)',
        'Advanced communications (25GB)',
        'Event management system',
        'Guest management',
        'Document templates',
        'Custom branding',
        'Priority support'
      ]
    },
    premium: { 
      name: 'Platform Pro', 
      homes: 'Up to 1000 homes', 
      yearlyPrice: 950,
      monthlyPrice: 85,
      yearlyPriceId: 'price_1SMCCBFuWSwki2tlIm6QbXYg',
      monthlyPriceId: 'price_1SMCCBFuWSwki2tl247tCsHS',
      displayPrice: '$85/mo',
      period: '/month',
      features: [
        'Everything in Standard',
        '✅ Custom Pages (included)',
        'AI-powered FAQ system (100GB)',
        'Advanced analytics',
        'Multiple board groups',
        'API access',
        'Dedicated support',
        'Custom integrations'
      ]
    }
  };

  const addOnOptions = [
    {
      id: 'budget_finance',
      name: 'Budget & Finance Reporting',
      yearlyPrice: 100,
      monthlyPrice: 9,
      yearlyPriceId: 'price_1SMGoUFuWSwki2tlxNU0ooW4',
      monthlyPriceId: 'price_1SMGoUFuWSwki2tlCRZ07YvQ',
      description: 'Comprehensive financial tracking and reporting tools',
      includedIn: ['standard', 'premium']  // Grey out if these plans selected
    },
    {
      id: 'amenity_management',
      name: 'Amenity Management',
      yearlyPrice: 100,
      monthlyPrice: 9,
      yearlyPriceId: 'price_1SMGl7FuWSwki2tllFtVv769',
      monthlyPriceId: 'price_1SMGl7FuWSwki2tlyqPc3hGO',
      description: 'Booking system for pools, clubhouse, tennis courts, and other amenities'
    },
    {
      id: 'voting_platform',
      name: 'Voting Platform',
      yearlyPrice: 100,
      monthlyPrice: 9,
      yearlyPriceId: 'price_1SMCDMFuWSwki2tlCrkHg3O4',
      monthlyPriceId: 'price_1SMCDMFuWSwki2tl3UQNumvm',
      description: 'Digital voting system for board elections and community decisions'
    },
    {
      id: 'custom_pages',
      name: 'Custom Pages',
      yearlyPrice: 100,
      monthlyPrice: 9,
      yearlyPriceId: 'price_1SMC9TFuWSwki2tlRjooENO7',
      monthlyPriceId: 'price_1SMC9TFuWSwki2tlPoGxqADM',
      description: 'Custom layouts, pages, and enhanced branding options for your community',
      includedIn: ['premium']  // Grey out if Pro selected
    },
    {
      id: 'extra_homes',
      name: 'Extra Homes',
      yearlyPrice: 35,
      monthlyPrice: 3,
      yearlyPriceId: 'price_1SMC4xFuWSwki2tlOme3A2MV',
      monthlyPriceId: 'price_1SMC5YFuWSwki2tlIj1raJk2',
      description: 'Expand your community capacity with additional home accounts',
      quantityBased: true
    },
    {
      id: 'extra_storage',
      name: 'Additional Storage (+10GB)',
      yearlyPrice: 30,
      monthlyPrice: 2.5,
      yearlyPriceId: 'price_1SMC6VFuWSwki2tlaoobbnZB',
      monthlyPriceId: 'price_1SMC6VFuWSwki2tlRjooENO7',
      description: 'Add 10GB of storage per unit for meeting minutes, architectural plans, and files',
      quantityBased: true
    },
    {
      id: 'guest_management',
      name: 'Guest Management',
      yearlyPrice: 50,
      monthlyPrice: 5,
      yearlyPriceId: 'price_1SMC3XFuWSwki2tlPNmuBNq1',
      monthlyPriceId: 'price_1SMC44FuWSwki2tlh7wspmYu',
      description: 'Register and track community guests, visitor parking, and access control',
      comingSoon: true
    }
  ];

  const amenityOptions = [
    'Pool', 'Gym/Fitness Center', 'Clubhouse', 'Tennis Court', 'Playground', 
    'Walking Trails', 'Dog Park', 'Business Center', 'Event Space', 'Security Gate',
    'Mailroom', 'Storage Units', 'Parking Garage', 'Other'
  ];

  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 
    'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 
    'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const handleInputChange = (section, field, value) => {
    if (section === 'hoa') {
      setHOAData(prev => ({ ...prev, [field]: value }));
    } else if (section === 'community') {
      setCommunityDetails(prev => ({ ...prev, [field]: value }));
    } else if (section === 'board') {
      setBoardSetup(prev => ({ ...prev, [field]: value }));
    } else if (section === 'operational') {
      setOperationalSetup(prev => ({ ...prev, [field]: value }));
    } else if (section === 'payment') {
      setPaymentData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Function to determine timezone from state
  const getTimezoneFromState = (state) => {
    const timezoneMap = {
      // Eastern Time
      'CT': 'America/New_York', 'DE': 'America/New_York', 'FL': 'America/New_York',
      'GA': 'America/New_York', 'ME': 'America/New_York', 'MD': 'America/New_York',
      'MA': 'America/New_York', 'NH': 'America/New_York', 'NJ': 'America/New_York',
      'NY': 'America/New_York', 'NC': 'America/New_York', 'OH': 'America/New_York',
      'PA': 'America/New_York', 'RI': 'America/New_York', 'SC': 'America/New_York',
      'VT': 'America/New_York', 'VA': 'America/New_York', 'WV': 'America/New_York',
      // Central Time
      'AL': 'America/Chicago', 'AR': 'America/Chicago', 'IL': 'America/Chicago',
      'IA': 'America/Chicago', 'KS': 'America/Chicago', 'LA': 'America/Chicago',
      'MN': 'America/Chicago', 'MS': 'America/Chicago', 'MO': 'America/Chicago',
      'NE': 'America/Chicago', 'OK': 'America/Chicago', 'SD': 'America/Chicago',
      'TX': 'America/Chicago', 'WI': 'America/Chicago',
      // Mountain Time
      'AZ': 'America/Phoenix', // Arizona doesn't observe DST
      'CO': 'America/Denver', 'MT': 'America/Denver', 'NM': 'America/Denver',
      'UT': 'America/Denver', 'WY': 'America/Denver',
      // Pacific Time
      'CA': 'America/Los_Angeles', 'NV': 'America/Los_Angeles',
      'OR': 'America/Los_Angeles', 'WA': 'America/Los_Angeles',
      // Alaska Time
      'AK': 'America/Anchorage',
      // Hawaii Time
      'HI': 'Pacific/Honolulu'
    };
    return timezoneMap[state] || 'America/Chicago'; // Default to Central
  };

  const handleZipCodeChange = (value) => {
    // If zip code is 5 digits, try to auto-fill city, state, and timezone
    if (value && value.length === 5 && /^\d{5}$/.test(value)) {
      try {
        const locationData = zipcodes.lookup(value);
        if (locationData) {
          const timezone = getTimezoneFromState(locationData.state);
          // Update zip, city, state, and timezone in one call to prevent multiple re-renders
          setHOAData(prev => ({ 
            ...prev, 
            zip_code: value,
            city: locationData.city || prev.city,
            state: locationData.state || prev.state,
            timezone: timezone
          }));
          console.log(`Auto-set timezone to ${timezone} based on state ${locationData.state}`);
          return; // Exit early if successful
        }
      } catch (error) {
        // Silently handle errors - user can still enter manually
        console.log('Zip code lookup failed:', error);
      }
    }
    
    // Fallback: just update the zip code field
    setHOAData(prev => ({ ...prev, zip_code: value }));
  };

  const updateBoardPosition = (index, field, value) => {
    setBoardSetup(prev => ({
      ...prev,
      positions: prev.positions.map((pos, i) => 
        i === index ? { ...pos, [field]: value } : pos
      )
    }));
  };

  const toggleAmenity = (amenity) => {
    setCommunityDetails(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const toggleAddOn = (addonId) => {
    setSelectedAddOns(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const calculateTotalPrice = () => {
    // Get base plan price based on payment frequency
    const plan = pricingPlans[selectedPlan];
    const planPrice = paymentFrequency === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
    
    let addOnPrice = 0;
    
    selectedAddOns.forEach(addonId => {
      const addon = addOnOptions.find(a => a.id === addonId);
      if (!addon) return;
      
      // Skip add-ons that are included in the selected plan
      if (addon.includedIn && addon.includedIn.includes(selectedPlan)) {
        return;
      }

      const perUnit = paymentFrequency === 'yearly' ? addon.yearlyPrice : addon.monthlyPrice;

      if (addon.quantityBased) {
        if (addonId === 'extra_homes') {
          addOnPrice += perUnit * (parseInt(extraHomesQuantity, 10) || 1);
        } else if (addonId === 'extra_storage') {
          addOnPrice += perUnit * (parseInt(extraStorageQuantity, 10) || 1);
        }
      } else {
        addOnPrice += perUnit;
      }
    });
    
    const subtotal = planPrice + addOnPrice;
    
    // Apply coupon discount if exists
    if (appliedCoupon) {
      if (appliedCoupon.type === 'percentage') {
        return Math.round(subtotal * (1 - appliedCoupon.discount / 100));
      } else if (appliedCoupon.type === 'fixed') {
        return Math.max(0, subtotal - appliedCoupon.discount);
      } else if (appliedCoupon.type === 'free') {
        return 0;
      }
    }
    
    return subtotal;
  };

  const calculateTotalHomes = () => {
    const planHomes = parseInt((pricingPlans[selectedPlan].homes.match(/\d+/) || ["0"])[0], 10);
    const extraHomes = selectedAddOns.includes('extra_homes') ? (extraHomesQuantity * 1) : 0;
    return planHomes + extraHomes;
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      return { success: false, message: 'Please enter a coupon code' };
    }

    try {
      // Try discount codes first
      try {
        const discountResponse = await axios.get(`/api/discount-codes/validate/${couponCode}`);
        
        if (discountResponse.data.valid) {
          const coupon = discountResponse.data.code;
          setAppliedCoupon(coupon);
          return { success: true, message: `Coupon applied: ${coupon.description}` };
        }
      } catch (discountError) {
        console.log('Not a discount code, checking trial codes...');
      }
      
      // If not a discount code, try trial codes
      try {
        const trialResponse = await axios.post(`/api/registration/validate-code`, {
          code: couponCode,
          email: hoaData.contact_email || 'temp@example.com',
          address: hoaData.address || '',
          city: hoaData.city || '',
          state: hoaData.state || '',
          zip: hoaData.zip_code || ''
        });
        
        if (trialResponse.data.valid && trialResponse.data.is_free_trial) {
          const trialMonths = trialResponse.data.trial_duration_months || 1;
          const coupon = {
            type: 'delayed_billing',
            delay_months: trialMonths,
            discount: 0,
            description: `${trialMonths} Month${trialMonths > 1 ? 's' : ''} Free Trial`
          };
          setAppliedCoupon(coupon);
          return { success: true, message: `Trial code applied: ${trialMonths} months free!` };
        }
      } catch (trialError) {
        console.log('Not a trial code either');
      }
      
      return { success: false, message: 'Invalid coupon or trial code' };
    } catch (error) {
      console.error('Code validation error:', error);
      return { success: false, message: 'Failed to validate code' };
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const saveQuote = async () => {
    setLoading(true);
    setError('');

    try {
      const quoteData = {
        hoa_data: hoaData,
        community_details: communityDetails,
        board_setup: boardSetup,
        operational_setup: operationalSetup,
        selected_plan: selectedPlan,
        selected_addons: selectedAddOns,
        total_amount: calculateTotalPrice(),
        contact_email: hoaData.contact_email
      };

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/setup/save-quote`, quoteData);
      
      if (response.data.success) {
        setSavedQuoteCode(response.data.quote_code);
        setError('');
      }
      
    } catch (err) {
      console.error('Failed to save quote:', err);
      setError((err.response && err.response.data && err.response.data.detail) || 'Failed to save quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRecommendedPlan = () => {
    const propertyCount = parseInt(hoaData.total_properties) || 0;
    if (propertyCount <= 100) return 'basic';
    if (propertyCount <= 300) return 'standard';
    return 'premium';
  };

  const getPricingSuggestion = () => {
    const propertyCount = parseInt(hoaData.total_properties) || 0;
    const recommended = getRecommendedPlan();
    
    if (propertyCount <= 100) {
      return {
        title: "Perfect Fit: Basic Plan",
        message: `With ${propertyCount} properties, our Basic plan covers your needs perfectly.`,
        tip: "💡 You can always upgrade later if you need more features!"
      };
    } else if (propertyCount <= 300) {
      return {
        title: "Recommended: Standard Plan", 
        message: `With ${propertyCount} properties, the Standard plan offers the best value.`,
        tip: "💰 Save money: Choose Basic + Extra Accounts add-on if you don't need all Standard features!"
      };
    } else {
      const extraHomes = Math.ceil((propertyCount - 300) / 100);
      return {
        title: "Best Option: Premium Plan",
        message: `With ${propertyCount} properties, Premium plan provides full enterprise features.`,
        tip: `💰 Alternative: Standard plan + ${extraHomes}x Extra Accounts = ${300 + (extraHomes * 100)} homes for $${499 + (extraHomes * 50)}/year vs Premium at $1020/year`
      };
    }
  };

  const validateStep = (step) => {
    const errors = [];
    
    switch (step) {
      case 1:
        if (!hoaData.name.trim()) errors.push("HOA Name is required");
        if (!hoaData.address.trim()) errors.push("HOA Address is required");
        if (!hoaData.city.trim()) errors.push("City is required");
        if (!hoaData.state) errors.push("State is required");
        if (!hoaData.zip_code.trim()) errors.push("ZIP Code is required");
        if (!hoaData.contact_email.trim()) errors.push("Contact Email is required");
        if (!hoaData.total_properties || hoaData.total_properties < 1) errors.push("Total Properties must be at least 1");
        
        // Email validation
        if (hoaData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(hoaData.contact_email)) {
          errors.push("Please enter a valid email address");
        }
        break;
        
      case 2:
        if (!communityDetails.meeting_frequency) errors.push("Meeting Frequency is required");
        if (!communityDetails.fiscal_year_end) errors.push("Fiscal Year End is required");
        
        // Amenities validation - check if has_amenities is true but no amenities selected
        if (communityDetails.has_amenities && (!communityDetails.amenities || communityDetails.amenities.length === 0)) {
          errors.push("Please select at least one amenity or uncheck 'Our community has amenities'");
        }
        break;
        
      case 3:
        const filledPositions = boardSetup.positions.filter(pos => pos.filled);
        const validPositions = filledPositions.filter(pos => pos.name.trim() && pos.email.trim());
        
        // Also check for positions that have name/email but aren't marked as filled (auto-mark them)
        const unfilledButCompleted = boardSetup.positions.filter(pos => 
          !pos.filled && pos.name.trim() && pos.email.trim()
        );
        
        // Auto-mark positions as filled if they have both name and email
        if (unfilledButCompleted.length > 0) {
          unfilledButCompleted.forEach((pos, index) => {
            const posIndex = boardSetup.positions.findIndex(p => p === pos);
            updateBoardPosition(posIndex, 'filled', true);
          });
        }
        
        const totalValidPositions = validPositions.length + unfilledButCompleted.length;
        
        if (filledPositions.length === 0 && totalValidPositions === 0) {
          errors.push("Please add at least one board member with name and email address");
        } else if (totalValidPositions === 0) {
          errors.push("At least one board member must have both name and email address");
        }
        
        // Check individual positions for partial data
        filledPositions.forEach((pos, index) => {
          if (!pos.name.trim()) {
            errors.push(`${pos.title}: Name is required`);
          }
          if (!pos.email.trim()) {
            errors.push(`${pos.title}: Email is required`);
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pos.email)) {
            errors.push(`${pos.title}: Please enter a valid email address`);
          }
        });
        break;
        
      case 4:
        if (!operationalSetup.state) errors.push("State is required");
        if (!operationalSetup.incorporation_status) errors.push("Incorporation Status is required");
        break;
        
      case 5:
        if (!selectedPlan) errors.push("Please select a pricing plan");
        break;
        
      case 6:
        // Authorization Agreement validation
        if (!authorizationData.signatory_name.trim()) errors.push("Signatory Name is required");
        if (!authorizationData.signatory_title.trim()) errors.push("Signatory Title is required");
        if (!authorizationData.signatory_email.trim()) errors.push("Signatory Email is required");
        if (!authorizationData.agreed_authority) errors.push("You must affirm authority to bind the Association");
        if (!authorizationData.agreed_terms) errors.push("You must agree to the Terms of Service");
        if (!authorizationData.agreed_privacy) errors.push("You must agree to the Privacy Policy");
        break;
        
      case 7:
        // Step 7 is now just confirmation - no validation needed
        // The Stripe checkout will handle payment validation
        break;
        
      default:
        break;
    }
    
    return { isValid: errors.length === 0, errors };
  };

  const getStepValidationErrors = (step) => {
    const validation = validateStep(step);
    return validation.errors;
  };

  const nextStep = () => {
    const validation = validateStep(currentStep);
    
    if (validation.isValid) {
      setCurrentStep(prev => prev + 1);
      setError('');
    } else {
      const errorMessage = `Please fix the following issues:\n\n${validation.errors.map((err, index) => `${index + 1}. ${err}`).join('\n')}`;
      setError(errorMessage);
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Create comprehensive HOA data
      // Sanitize data - convert empty strings to null for optional fields
      const sanitizeData = (obj) => {
        // Handle arrays - don't convert them to objects
        if (Array.isArray(obj)) {
          return obj.map(item => 
            typeof item === 'object' && item !== null ? sanitizeData(item) : item
          );
        }
        
        // Handle objects
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value === '') {
            sanitized[key] = null;
          } else if (Array.isArray(value)) {
            // Preserve arrays
            sanitized[key] = value;
          } else if (typeof value === 'object' && value !== null) {
            sanitized[key] = sanitizeData(value);
          } else {
            sanitized[key] = value;
          }
        }
        return sanitized;
      };

      const completeHOAData = sanitizeData({
        hoa_data: hoaData,
        community_details: communityDetails,
        board_setup: boardSetup,
        operational_setup: operationalSetup,
        authorization_data: authorizationData,
        selected_plan: selectedPlan,
        selected_addons: selectedAddOns,
        payment_frequency: paymentFrequency,
        extra_homes_quantity: extraHomesQuantity,
        extra_storage_quantity: extraStorageQuantity,
        total_price: calculateTotalPrice(),
        coupon_code: appliedCoupon ? {
          code: couponCode,
          type: appliedCoupon.type,
          delay_months: appliedCoupon.delay_months,
          discount: appliedCoupon.discount
        } : null
      });

      console.log('Creating HOA signup with data:', completeHOAData);
      
      // Check if payment is required
      const totalPrice = calculateTotalPrice();
      
      // Only skip Stripe for truly free signups (no trial period)
      const isCompletelyFree =
        appliedCoupon &&
        appliedCoupon.type === 'free' &&
        (!appliedCoupon.delay_months || appliedCoupon.delay_months === 0);
      
      const usesDelayedBilling =
        appliedCoupon &&
        appliedCoupon.type === 'delayed_billing' &&
        appliedCoupon.delay_months > 0;
      
      if (isCompletelyFree || totalPrice === 0) {
        // ✅ Lifetime-free or manually comped → skip Stripe
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/setup/create-hoa`, 
          completeHOAData
        );
        
        // Store the HOA data for onboarding
        setCreatedHOAData({
          ...completeHOAData,
          hoa_code: response.data.hoa_code,
          hoa_id: response.data.hoa.id,
          board_member_credentials: response.data.board_member_credentials || []
        });
        
        setCurrentStep(9); // Success step
        setLoading(false);
      } else {
        // Paid signup - redirect to Stripe checkout
        console.log('💳 Initiating Stripe checkout for paid signup...');
        
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/setup/create-checkout-session`,
          completeHOAData
        );
        
        if (response.data.success && response.data.checkout_url) {
          console.log('✅ Redirecting to Stripe checkout...');
          // Redirect to Stripe checkout
          window.location.href = response.data.checkout_url;
        } else {
          throw new Error('Failed to create checkout session');
        }
      }
      
    } catch (err) {
      console.error('Failed to create HOA:', err);
      setError((err.response && err.response.data && err.response.data.detail) || 'Failed to initiate signup. Please try again.');
      setLoading(false);
    }
  };

  // If showing onboarding, render the checklist
  if (showOnboarding) {
    return (
      <HOAOnboardingChecklist 
        hoaData={createdHOAData}
        onComplete={() => onComplete('admin')}
      />
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Basic HOA Information</h2>
              <p className="text-gray-600 mb-6">Let's start with the essential details about your homeowners association.</p>
              
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    HOA Name *
                  </label>
                  <input
                    type="text"
                    value={hoaData.name}
                    onChange={(e) => handleInputChange('hoa', 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Sunnydale Homeowners Association"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    value={hoaData.address}
                    onChange={(e) => handleInputChange('hoa', 'address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="123 Community Blvd"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      value={hoaData.zip_code}
                      onChange={(e) => handleZipCodeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="78745"
                      maxLength="5"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      value={hoaData.city}
                      onChange={(e) => handleInputChange('hoa', 'city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Austin"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State *
                    </label>
                    <select
                      value={hoaData.state}
                      onChange={(e) => handleInputChange('hoa', 'state', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select State</option>
                      {usStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Email *
                    </label>
                    <input
                      type="email"
                      value={hoaData.contact_email}
                      onChange={(e) => handleInputChange('hoa', 'contact_email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="board@sunnydalehoa.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Properties *
                    </label>
                    <input
                      type="number"
                      value={hoaData.total_properties}
                      onChange={(e) => handleInputChange('hoa', 'total_properties', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="150"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Community Type
                  </label>
                  <select
                    value={hoaData.community_type}
                    onChange={(e) => handleInputChange('hoa', 'community_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="residential">Residential Community</option>
                    <option value="mixed">Mixed Use (Residential + Commercial)</option>
                    <option value="commercial">Commercial Only</option>
                    <option value="condo">Condominium Association</option>
                    <option value="townhome">Townhome Community</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Management Status
                  </label>
                  <select
                    value={hoaData.current_management}
                    onChange={(e) => handleInputChange('hoa', 'current_management', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="self_managed">Self-Managed by Board</option>
                    <option value="management_company">Professional Management Company</option>
                    <option value="transitioning">Transitioning Management</option>
                    <option value="new">New HOA - No Management Yet</option>
                  </select>
                </div>

                {hoaData.current_management === 'management_company' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Management Company Name
                    </label>
                    <input
                      type="text"
                      value={hoaData.management_company}
                      onChange={(e) => handleInputChange('hoa', 'management_company', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="ABC Property Management"
                    />
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website (Optional)
                    </label>
                    <input
                      type="url"
                      value={hoaData.website}
                      onChange={(e) => handleInputChange('hoa', 'website', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="https://www.sunnydalehoa.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Established Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={hoaData.established_date}
                      onChange={(e) => handleInputChange('hoa', 'established_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Community Description (Optional)
                  </label>
                  <textarea
                    value={hoaData.description}
                    onChange={(e) => handleInputChange('hoa', 'description', e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Brief description of your community, amenities, and unique features..."
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Community Details & Operations</h2>
              <p className="text-gray-600 mb-6">Tell us about your community's amenities, committees, and operational structure.</p>
              
              <div className="space-y-6">
                {/* Amenities Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">Community Amenities</h3>
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={communityDetails.has_amenities}
                        onChange={(e) => handleInputChange('community', 'has_amenities', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Our community has amenities</span>
                    </label>
                  </div>
                  
                  {communityDetails.has_amenities && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {amenityOptions.map((amenity) => (
                        <label key={amenity} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={communityDetails.amenities.includes(amenity)}
                            onChange={() => toggleAmenity(amenity)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{amenity}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Committees Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">Community Committees</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={communityDetails.has_architectural_committee}
                        onChange={(e) => handleInputChange('community', 'has_architectural_committee', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Architectural Review Committee (ARC)</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={communityDetails.has_landscape_committee}
                        onChange={(e) => handleInputChange('community', 'has_landscape_committee', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Landscape Committee</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={communityDetails.has_social_committee}
                        onChange={(e) => handleInputChange('community', 'has_social_committee', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Social/Events Committee</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={communityDetails.has_fines_committee}
                        onChange={(e) => handleInputChange('community', 'has_fines_committee', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Grievance Committee</span>
                    </label>
                  </div>
                </div>

                {/* Meeting & Financial Operations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Board Meeting Frequency *
                    </label>
                    <select
                      value={communityDetails.meeting_frequency}
                      onChange={(e) => handleInputChange('community', 'meeting_frequency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="biannually">Twice per Year</option>
                      <option value="annually">Annually</option>
                      <option value="as_needed">As Needed</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fiscal Year End *
                    </label>
                    <select
                      value={communityDetails.fiscal_year_end}
                      onChange={(e) => handleInputChange('community', 'fiscal_year_end', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="december">December 31</option>
                      <option value="june">June 30</option>
                      <option value="march">March 31</option>
                      <option value="september">September 30</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assessment Frequency
                    </label>
                    <select
                      value={communityDetails.assessment_frequency}
                      onChange={(e) => handleInputChange('community', 'assessment_frequency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Dues Amount (Optional)
                    </label>
                    <input
                      type="number"
                      value={communityDetails.current_dues_amount}
                      onChange={(e) => handleInputChange('community', 'current_dues_amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={
                        communityDetails.assessment_frequency === 'monthly' ? '250' :
                        communityDetails.assessment_frequency === 'quarterly' ? '750' :
                        communityDetails.assessment_frequency === 'annually' ? '3000' : '250'
                      }
                    />
                    {communityDetails.current_dues_amount && communityDetails.assessment_frequency !== 'monthly' && (
                      <p className="text-sm text-gray-500 mt-1">
                        Monthly equivalent: ${
                          communityDetails.assessment_frequency === 'quarterly' 
                            ? Math.round(parseFloat(communityDetails.current_dues_amount || 0) / 3)
                            : communityDetails.assessment_frequency === 'annually'
                            ? Math.round(parseFloat(communityDetails.current_dues_amount || 0) / 12)
                            : communityDetails.current_dues_amount
                        }
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional Operations */}
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={communityDetails.has_special_assessments}
                      onChange={(e) => handleInputChange('community', 'has_special_assessments', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Community has special assessments</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={communityDetails.has_violations}
                      onChange={(e) => handleInputChange('community', 'has_violations', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Community enforces violation fines</span>
                  </label>
                </div>

                {/* Maintenance Responsibilities */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">HOA Maintenance Responsibilities</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="maintenance_type"
                        value="none"
                        checked={communityDetails.maintenance_type === 'none'}
                        onChange={(e) => handleInputChange('community', 'maintenance_type', e.target.value)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">No HOA maintenance responsibilities</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="maintenance_type"
                        value="entrance_only"
                        checked={communityDetails.maintenance_type === 'entrance_only'}
                        onChange={(e) => handleInputChange('community', 'maintenance_type', e.target.value)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Front entrance/sign only</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="maintenance_type"
                        value="common_areas"
                        checked={communityDetails.maintenance_type === 'common_areas'}
                        onChange={(e) => handleInputChange('community', 'maintenance_type', e.target.value)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Common areas (clubhouse, lakes, parks, etc.)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Board of Directors Setup</h2>
              <p className="text-gray-600 mb-6">Configure your board structure and add initial board member information.</p>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Board Size
                    </label>
                    <select
                      value={boardSetup.board_size}
                      onChange={(e) => {
                        const size = parseInt(e.target.value);
                        handleInputChange('board', 'board_size', e.target.value);
                        // Adjust positions array based on board size
                        const newPositions = [...boardSetup.positions];
                        if (size > newPositions.length) {
                          for (let i = newPositions.length; i < size; i++) {
                            newPositions.push({ title: `Board Member ${i + 1}`, filled: false, name: '', email: '', term_length: '2' });
                          }
                        }
                        setBoardSetup(prev => ({ 
                          ...prev, 
                          positions: newPositions.slice(0, size).map((pos, index) => {
                            if (index < 4) {
                              // Keep original titles for standard positions
                              return pos;
                            } else {
                              // For additional positions, set default titles
                              return {
                                ...pos,
                                title: index === 4 ? 'Member at Large' : `Board Member ${index + 1}`,
                                customTitle: pos.customTitle || ''
                              };
                            }
                          })
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="3">3 Members</option>
                      <option value="5">5 Members</option>
                      <option value="7">7 Members</option>
                      <option value="9">9 Members</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Election Cycle
                    </label>
                    <select
                      value={boardSetup.election_cycle}
                      onChange={(e) => handleInputChange('board', 'election_cycle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="annual">Annual Elections</option>
                      <option value="biennial">Every 2 Years</option>
                      <option value="varied">Staggered Terms</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Next Election Date
                    </label>
                    <input
                      type="date"
                      value={boardSetup.next_election_date || ''}
                      onChange={(e) => handleInputChange('board', 'next_election_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank if unknown - you can schedule elections later</p>
                  </div>
                  
                  <div className="flex items-center pt-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={boardSetup.term_limits}
                        onChange={(e) => handleInputChange('board', 'term_limits', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Term Limits</span>
                    </label>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Board Positions</h3>
                  <div className="space-y-3 mb-4">
                    <p className="text-sm text-gray-600">Add at least one board member to continue. You can add more later.</p>
                    <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                      💡 <strong>Note:</strong> Board members can be added later if not known yet. You can also add committee members with different access levels during setup.
                    </p>
                    
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="bod-homeowners"
                          name="bod_homeowner_requirement"
                          value={true}
                          checked={boardSetup.require_homeowner === true}
                          onChange={(e) => handleInputChange('board', 'require_homeowner', true)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <label htmlFor="bod-homeowners" className="ml-2 text-sm text-gray-700">
                          Board members must be homeowners
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="bod-non-homeowners"
                          name="bod_homeowner_requirement"
                          value={false}
                          checked={boardSetup.require_homeowner === false}
                          onChange={(e) => handleInputChange('board', 'require_homeowner', false)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <label htmlFor="bod-non-homeowners" className="ml-2 text-sm text-gray-700">
                          Non-homeowners can serve on board
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {boardSetup.positions.map((position, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{position.title}</h4>
                            {(position.title === 'Member at Large' || position.title.startsWith('Board Member')) && (
                              <div className="mt-2">
                                <input
                                  type="text"
                                  placeholder={`Custom title for ${position.title} (optional)`}
                                  value={position.customTitle}
                                  onChange={(e) => updateBoardPosition(index, 'customTitle', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  e.g., "Compliance Officer", "Communications Director"
                                </p>
                              </div>
                            )}
                          </div>
                          <label className="flex items-center ml-4">
                            <input
                              type="checkbox"
                              checked={position.filled}
                              onChange={(e) => updateBoardPosition(index, 'filled', e.target.checked)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Position Filled</span>
                          </label>
                        </div>
                        
                        {position.filled && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <input
                                type="text"
                                placeholder="Full Name"
                                value={position.name}
                                onChange={(e) => updateBoardPosition(index, 'name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <input
                                type="email"
                                placeholder="Email Address"
                                value={position.email}
                                onChange={(e) => updateBoardPosition(index, 'email', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <select
                                value={position.term_length}
                                onChange={(e) => updateBoardPosition(index, 'term_length', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              >
                                <option value="1">1 Year Term</option>
                                <option value="2">2 Year Term</option>
                                <option value="3">3 Year Term</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Legal & Operational Setup</h2>
              <p className="text-gray-600 mb-6">Help us understand your HOA's legal structure and operational requirements.</p>
              
              <div className="space-y-6">
                {/* Legal Structure */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">Legal Structure</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State *
                      </label>
                      <select
                        value={operationalSetup.state}
                        onChange={(e) => handleInputChange('operational', 'state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select State</option>
                        {usStates.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Incorporation Status *
                      </label>
                      <select
                        value={operationalSetup.incorporation_status}
                        onChange={(e) => handleInputChange('operational', 'incorporation_status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="incorporated">Incorporated</option>
                        <option value="unincorporated">Unincorporated</option>
                        <option value="unknown">Not Sure</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Documents */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">Governing Documents</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={operationalSetup.has_cc_rs}
                        onChange={(e) => handleInputChange('operational', 'has_cc_rs', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">CC&Rs (Covenants, Conditions & Restrictions)</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={operationalSetup.has_bylaws}
                        onChange={(e) => handleInputChange('operational', 'has_bylaws', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Bylaws</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={operationalSetup.has_rules_regulations}
                        onChange={(e) => handleInputChange('operational', 'has_rules_regulations', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Rules & Regulations</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={operationalSetup.needs_document_help}
                        onChange={(e) => handleInputChange('operational', 'needs_document_help', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">We need help creating/updating our documents</span>
                    </label>
                  </div>
                </div>

                {/* Financial Setup */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">Financial Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Operating Fund Balance (Optional)
                      </label>
                      <input
                        type="number"
                        value={operationalSetup.operating_fund_balance}
                        onChange={(e) => handleInputChange('operational', 'operating_fund_balance', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="50000"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reserve Fund Balance (Optional)
                      </label>
                      <input
                        type="number"
                        value={operationalSetup.reserve_fund_balance}
                        onChange={(e) => handleInputChange('operational', 'reserve_fund_balance', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="25000"
                      />
                    </div>
                  </div>
                  
                  {/* Removed: has_reserve_study and has_insurance checkboxes */}

                </div>

                {/* Professional Contacts */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">Professional Contacts (Optional)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Attorney Contact
                      </label>
                      <input
                        type="text"
                        value={operationalSetup.attorney_contact}
                        onChange={(e) => handleInputChange('operational', 'attorney_contact', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Law Firm Name or Contact"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Accountant Contact
                      </label>
                      <input
                        type="text"
                        value={operationalSetup.accountant_contact}
                        onChange={(e) => handleInputChange('operational', 'accountant_contact', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Accounting Firm or CPA"
                      />
                    </div>
                  </div>
                  
                  {/* Accounting Services Checkbox */}
                  <div className="mt-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={operationalSetup.needs_accounting_services}
                        onChange={(e) => handleInputChange('operational', 'needs_accounting_services', e.target.checked)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        I need accounting services
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 ml-6 mt-1">
                      Check this if you would like assistance with accounting services for your HOA
                    </p>
                  </div>
                </div>

                {/* Removed: Communication Preferences section */}

              </div>
            </div>
          </div>
        );

      case 5:
        const pricingSuggestion = getPricingSuggestion();
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
              <p className="text-gray-600 mb-6">Select the plan that best fits your community's needs and size.</p>
              
              {/* Smart Pricing Recommendation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-blue-800">{pricingSuggestion.title}</h3>
                    <p className="text-sm text-blue-700 mt-1">{pricingSuggestion.message}</p>
                    <p className="text-sm text-blue-600 mt-2">{pricingSuggestion.tip}</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {Object.entries(pricingPlans).map(([key, plan]) => (
                  <div
                    key={key}
                    className={`border-2 rounded-lg p-6 cursor-pointer transition-all relative ${
                      selectedPlan === key 
                        ? 'border-indigo-600 bg-indigo-50 shadow-lg' 
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                    onClick={() => setSelectedPlan(key)}
                  >
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                          Most Popular
                        </span>
                      </div>
                    )}
                    
                    <div className="text-center">
                      <h3 className="text-lg font-semibold mb-2">{plan.name}</h3>
                      <p className="text-sm text-gray-600 mb-4">{plan.homes}</p>
                      <div className="mb-6">
                        {paymentFrequency === 'yearly' ? (
                          <span className="text-2xl font-bold">${plan.yearlyPrice}/year</span>
                        ) : (
                          <span className="text-2xl font-bold">${plan.monthlyPrice}/month</span>
                        )}
                      </div>
                      
                      <ul className="text-left space-y-2 mb-6">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-sm text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <div className={`w-6 h-6 rounded-full border-2 mx-auto ${
                        selectedPlan === key 
                          ? 'bg-indigo-600 border-indigo-600' 
                          : 'border-gray-300'
                      }`}>
                        {selectedPlan === key && (
                          <svg className="w-4 h-4 text-white m-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Payment Frequency Selection */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-testid="payment-frequency">
                <h3 className="text-lg font-semibold mb-4">Payment Frequency</h3>
                <p className="text-sm text-gray-600 mb-4">Choose how often you'd like to be billed for your subscription.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Yearly Payment Option */}
                  <div
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      paymentFrequency === 'yearly' 
                        ? 'border-indigo-600 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setPaymentFrequency('yearly')}
                  >
                    <div className="flex items-start">
                      <div className={`w-4 h-4 border-2 rounded-full mr-3 mt-1 flex items-center justify-center ${
                        paymentFrequency === 'yearly' ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'
                      }`}>
                        {paymentFrequency === 'yearly' && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">Annual Billing</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {selectedPlan === 'basic' && 'Pay $399 once per year (Save $21!)'}
                          {selectedPlan === 'standard' && 'Pay $499 once per year (Save $41!)'}
                          {selectedPlan === 'premium' && 'Pay $950 once per year (Save $70!)'}
                        </p>
                        {(selectedPlan === 'basic' || selectedPlan === 'standard' || selectedPlan === 'premium') && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {selectedPlan === 'basic' && '$21 Savings'}
                              {selectedPlan === 'standard' && '$41 Savings'}
                              {selectedPlan === 'premium' && '$20 Savings'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Monthly Payment Option */}
                  <div
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      paymentFrequency === 'monthly' 
                        ? 'border-indigo-600 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setPaymentFrequency('monthly')}
                  >
                    <div className="flex items-start">
                      <div className={`w-4 h-4 border-2 rounded-full mr-3 mt-1 flex items-center justify-center ${
                        paymentFrequency === 'monthly' ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'
                      }`}>
                        {paymentFrequency === 'monthly' && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">Monthly Billing</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {selectedPlan === 'basic' && 'Pay $35 each month'}
                          {selectedPlan === 'standard' && 'Pay $45 each month'}
                          {selectedPlan === 'premium' && 'Pay $85 each month'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedPlan === 'basic' && 'Total: $420/year (vs $399/year annual)'}
                          {selectedPlan === 'standard' && 'Total: $540/year (vs $499/year annual)'}
                          {selectedPlan === 'premium' && 'Pay as you go with monthly flexibility'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Add-On Options */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Add-On Features</h3>
                <p className="text-sm text-gray-600 mb-4">Enhance your plan with additional features tailored to your community's needs.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  {addOnOptions.map((addon) => {
                    const isIncluded = addon.includedIn && addon.includedIn.includes(selectedPlan);
                    const isComingSoon = addon.comingSoon;
                    const isDisabled = isIncluded || isComingSoon;
                    
                    return (
                      <div 
                        key={addon.id} 
                        className={`border rounded-lg p-4 ${
                          isDisabled 
                            ? 'border-gray-200 bg-gray-100 opacity-60' 
                            : 'border-gray-200'
                        }`}
                      >
                        <label className={`flex items-start ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={selectedAddOns.includes(addon.id)}
                            onChange={() => !isDisabled && toggleAddOn(addon.id)}
                            disabled={isDisabled}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-1 disabled:cursor-not-allowed"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <span className={`font-medium ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                                  {addon.name}
                                </span>
                                {isIncluded && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                    ✓ Included
                                  </span>
                                )}
                                {isComingSoon && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    Coming Soon
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                {addon.quantityBased && selectedAddOns.includes(addon.id) && (
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm text-gray-600">Qty:</span>
                                    <input
                                      type="number"
                                      min="1"
                                      max="999"
                                      value={addon.id === 'extra_homes' ? extraHomesQuantity : extraStorageQuantity}
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value, 10) || 1;
                                        if (addon.id === 'extra_homes') {
                                          setExtraHomesQuantity(val);
                                        } else if (addon.id === 'extra_storage') {
                                          setExtraStorageQuantity(val);
                                        }
                                      }}
                                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                  </div>
                                )}
                                {!isIncluded && (
                                  <span className={`font-semibold ${isDisabled ? 'text-gray-400' : 'text-indigo-600'}`}>
                                    {addon.quantityBased && selectedAddOns.includes(addon.id)
                                      ? `+$${(paymentFrequency === 'yearly' ? addon.yearlyPrice : addon.monthlyPrice) * (addon.id === 'extra_homes' ? extraHomesQuantity : extraStorageQuantity)}/${paymentFrequency === 'yearly' ? 'year' : 'mo'}`
                                      : `+$${paymentFrequency === 'yearly' ? addon.yearlyPrice : addon.monthlyPrice}/${paymentFrequency === 'yearly' ? 'year' : 'mo'}`
                                    }
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className={`text-sm mt-1 ${isDisabled ? 'text-gray-500' : 'text-gray-600'}`}>
                              {addon.description}
                            </p>
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Pricing Summary */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="font-medium text-gray-900">{pricingPlans[selectedPlan].name}</p>
                    <p className="text-sm text-gray-600">
                      Total Capacity: {calculateTotalHomes()} homes
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedAddOns.length > 0 && `${selectedAddOns.length} add-on${selectedAddOns.length > 1 ? 's' : ''} selected`}
                    </p>
                  </div>
                  <div className="text-right">
                    {appliedCoupon && appliedCoupon.type === 'free' ? (
                      <p className="text-2xl font-bold text-green-600">FREE</p>
                    ) : (
                      <div className="text-right">
                        <p className="text-2xl font-bold text-indigo-600">
                          ${calculateTotalPrice()}/{paymentFrequency === 'yearly' ? 'year' : 'month'}
                        </p>
                        {paymentFrequency === 'yearly' && Number.isFinite(calculateTotalPrice()) && (
                          <p className="text-sm text-gray-600">≈ ${Math.round(calculateTotalPrice() / 12)}/month</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Coupon Code Section */}
                <div className="border-t border-indigo-200 pt-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={async () => {
                        const result = await applyCoupon();
                        if (result.success) {
                          alert(result.message);
                        } else {
                          alert(result.message);
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                    >
                      Apply
                    </button>
                  </div>

                  {appliedCoupon && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-green-800">🎉 {appliedCoupon.description}</p>
                        <button
                          onClick={removeCoupon}
                          className="text-sm text-green-700 hover:text-green-800 underline"
                        >
                          Remove
                        </button>
                      </div>
                      {appliedCoupon.type === 'free' && (
                        <p className="text-xs text-green-600">Your HOA setup is completely free!</p>
                      )}
                      {appliedCoupon.type === 'delayed_billing' && appliedCoupon.delay_months && (
                        <div className="text-xs text-green-700 mt-1 space-y-1">
                          <p>✓ No payment required today</p>
                          <p className="font-medium">
                            First charge: {new Date(Date.now() + appliedCoupon.delay_months * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                          <p>Amount: ${calculateTotalPrice()}/{paymentFrequency === 'yearly' ? 'year' : 'month'}</p>
                        </div>
                      )}
                      {(appliedCoupon.type === 'percentage' || appliedCoupon.type === 'fixed') && (
                        <p className="text-xs text-green-700 mt-1">
                          You're saving ${appliedCoupon.type === 'percentage' 
                            ? Math.round(calculateTotalPrice() * appliedCoupon.discount / (100 - appliedCoupon.discount))
                            : appliedCoupon.discount
                          }!
                        </p>
                      )}
                    </div>
                  )}

                </div>
              </div>

              {/* Plan Flexibility Notice */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-green-800">Complete Flexibility</h4>
                    <p className="text-sm text-green-700 mt-1">
                      You can upgrade, downgrade, or add/remove features at any time. Start with what you need now and adjust as your community grows!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="max-w-4xl mx-auto">
            <AuthorizationAgreement 
              authData={authorizationData}
              onChange={setAuthorizationData}
            />
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Setup</h2>
              <p className="text-gray-600 mb-6">
                {appliedCoupon && appliedCoupon.type === 'free' 
                  ? 'Your HOA setup is free with the applied coupon!' 
                  : 'Review your selections and proceed to secure payment.'
                }
              </p>
              
              {/* Order Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
                
                {/* Selected Plan */}
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <div>
                    <p className="font-medium text-gray-900">{pricingPlans[selectedPlan].name}</p>
                    <p className="text-sm text-gray-600">{pricingPlans[selectedPlan].homes}</p>
                    <p className="text-xs text-gray-500">
                      Billed {paymentFrequency === 'yearly' ? 'annually' : 'monthly'}
                    </p>
                  </div>
                  <p className="font-semibold text-gray-900">
                    ${paymentFrequency === 'yearly' ? pricingPlans[selectedPlan].yearlyPrice : pricingPlans[selectedPlan].monthlyPrice}
                  </p>
                </div>
                
                {/* Selected Add-ons */}
                {selectedAddOns.length > 0 && (
                  <div className="py-3 space-y-2">
                    {selectedAddOns.map(addonId => {
                      const addon = addOnOptions.find(a => a.id === addonId);
                      if (!addon || (addon.includedIn && addon.includedIn.includes(selectedPlan))) return null;
                      
                      const price = paymentFrequency === 'yearly' ? addon.yearlyPrice : addon.monthlyPrice;
                      let quantity = 1;
                      if (addon.quantityBased) {
                        quantity = addonId === 'extra_homes' ? extraHomesQuantity : extraStorageQuantity;
                      }
                      
                      return (
                        <div key={addonId} className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-gray-900">{addon.name}</p>
                            {addon.quantityBased && (
                              <p className="text-xs text-gray-500">Quantity: {quantity}</p>
                            )}
                          </div>
                          <p className="text-sm text-gray-900">
                            +${price * quantity}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Total */}
                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-lg font-bold text-gray-900">Total</p>
                    <p className="text-xs text-gray-500">
                      {paymentFrequency === 'yearly' ? 'per year' : 'per month'}
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-indigo-600">
                    {appliedCoupon && appliedCoupon.type === 'free' ? (
                      <span className="text-green-600">FREE</span>
                    ) : (
                      `$${calculateTotalPrice()}`
                    )}
                  </p>
                </div>
                
                {appliedCoupon && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-green-800 mb-2">
                      🎉 Coupon applied: {appliedCoupon.description}
                    </p>
                    {appliedCoupon.type === 'delayed_billing' && appliedCoupon.delay_months && (
                      <div className="text-sm text-green-700 space-y-1">
                        <p className="font-medium">⏰ Delayed Billing Active</p>
                        <p>• No payment required today</p>
                        <p>• Your HOA will be created immediately</p>
                        <p>• First charge on: <span className="font-semibold">
                          {new Date(Date.now() + appliedCoupon.delay_months * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </span></p>
                        <p>• Amount: ${calculateTotalPrice()}/{paymentFrequency === 'yearly' ? 'year' : 'month'}</p>
                      </div>
                    )}
                    {appliedCoupon.type === 'free' && (
                      <p className="text-sm text-green-700">Your subscription is completely free!</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Payment Info */}
              {appliedCoupon && appliedCoupon.type === 'free' ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="mx-auto h-16 w-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
                    <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-green-800 mb-2">🎉 No Payment Required!</h3>
                  <p className="text-green-700 mb-4">
                    Your coupon code <code className="bg-green-100 px-2 py-1 rounded font-mono">{couponCode.toUpperCase()}</code> makes your HOA setup completely free.
                  </p>
                  <p className="text-sm text-green-600">
                    Click "Create My HOA" below to complete your setup!
                  </p>
                </div>
              ) : appliedCoupon && appliedCoupon.type === 'delayed_billing' ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                  <div className="mx-auto h-16 w-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
                    <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">⏰ No Payment Today!</h3>
                  <p className="text-blue-700 mb-4">
                    Your coupon code <code className="bg-blue-100 px-2 py-1 rounded font-mono">{couponCode.toUpperCase()}</code> gives you {appliedCoupon.delay_months} months free.
                  </p>
                  <div className="bg-white border border-blue-200 rounded-lg p-4 text-left">
                    <p className="text-sm text-blue-900 mb-2">
                      <strong>What happens next:</strong>
                    </p>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>✓ Your HOA will be created immediately</li>
                      <li>✓ Full access to all features during trial period</li>
                      <li>✓ First charge on: <strong>
                        {new Date(Date.now() + appliedCoupon.delay_months * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </strong></li>
                      <li>✓ Amount: ${calculateTotalPrice()}/{paymentFrequency === 'yearly' ? 'year' : 'month'}</li>
                    </ul>
                  </div>
                  <p className="text-sm text-blue-600 mt-4">
                    Click "Create My HOA" below to get started!
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">Secure Stripe Checkout</h4>
                      <p className="text-sm text-blue-800 mb-3">
                        When you click "Proceed to Payment", you'll be redirected to Stripe's secure checkout page to complete your payment.
                      </p>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>✓ 256-bit SSL encryption</li>
                        <li>✓ PCI compliant payment processing</li>
                        <li>✓ Your HOA will be created immediately after payment</li>
                        <li>✓ Receipt will be sent to {hoaData.contact_email}</li>
                        <li>✓ All payments BCC'd to info@hoamamanagerpro.com</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Review and Confirm</h2>
              
              <div className="space-y-6">
                {/* HOA Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">HOA Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Name:</p>
                      <p className="font-medium">{hoaData.name}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Properties:</p>
                      <p className="font-medium">{hoaData.total_properties} homes</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Address:</p>
                      <p className="font-medium">{hoaData.address}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Contact:</p>
                      <p className="font-medium">{hoaData.contact_email}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Community Type:</p>
                      <p className="font-medium">{hoaData.community_type.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Management:</p>
                      <p className="font-medium">{hoaData.current_management.replace('_', ' ')}</p>
                    </div>
                  </div>
                </div>

                {/* Board Setup Summary */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Board Configuration</h3>
                  <div className="text-sm">
                    <p className="text-gray-600 mb-2">Board Size: <span className="font-medium">{boardSetup.board_size} members</span></p>
                    <p className="text-gray-600 mb-3">Filled Positions:</p>
                    <div className="space-y-1">
                      {boardSetup.positions.filter(pos => pos.filled).map((pos, index) => (
                        <div key={index} className="flex justify-between">
                          <span>{pos.title}:</span>
                          <span className="font-medium">{pos.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Selected Plan */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Selected Plan</h3>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{pricingPlans[selectedPlan].name}</p>
                      <p className="text-sm text-gray-600">{pricingPlans[selectedPlan].homes}</p>
                      {selectedAddOns.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">Add-ons:</p>
                          <ul className="text-sm">
                            {selectedAddOns.map(addonId => {
                              const addon = addOnOptions.find(a => a.id === addonId);
                              return (
                                <li key={addonId} className="text-gray-700">
                                  • {addon.name} (+${addon.price}/year)
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-indigo-600">
                        ${calculateTotalPrice()}/{paymentFrequency === 'yearly' ? 'year' : 'month'}
                      </p>
                      {paymentFrequency === 'yearly' && Number.isFinite(calculateTotalPrice()) && (
                        <p className="text-sm text-gray-600">≈ ${Math.round(calculateTotalPrice() / 12)}/month</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Payment Method */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Payment Method</h3>
                  <p className="text-sm text-gray-600">**** **** **** {paymentData.cardNumber.slice(-4)}</p>
                  <p className="text-sm text-gray-600">{paymentData.name}</p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <svg className="w-5 h-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm text-blue-800">
                      <strong>What happens next?</strong>
                    </p>
                    <ul className="text-sm text-blue-700 mt-1 space-y-1">
                      <li>• Your HOA will be created with a unique access code</li>
                      <li>• Board members will receive admin access invitations</li>
                      <li>• You can immediately start adding homeowners</li>
                      <li>• 30-day free trial begins today</li>
                      <li>• Our support team will help with initial setup</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 9:
        return (
          <div className="text-center space-y-6">
            <div className="mx-auto h-16 w-16 bg-green-600 rounded-full flex items-center justify-center">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900">🎉 Welcome to HOA Manager Pro!</h2>
            <p className="text-xl text-gray-600">
              Your community portal has been successfully created.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-md mx-auto">
              <h3 className="font-semibold text-green-800 mb-3">Your HOA Details:</h3>
              <p className="text-green-700">Community: {hoaData.name}</p>
              <p className="text-green-700">HOA Code: <span className="font-mono bg-green-100 px-2 py-1 rounded">{(createdHOAData && createdHOAData.hoa_code) || 'GENERATING...'}</span></p>
              <p className="text-green-700">Plan: {pricingPlans[selectedPlan].name}</p>
              <p className="text-green-700">Annual Cost: ${calculateTotalPrice()}</p>
              <p className="text-sm text-green-600 mt-3">
                Share your HOA code with residents so they can register and join your community portal.
              </p>
            </div>

            {/* Login Credentials Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
              <h3 className="font-semibold text-blue-800 mb-3">🔑 Your Login Credentials</h3>
              
              {(createdHOAData && createdHOAData.board_member_credentials && createdHOAData.board_member_credentials.length > 0) ? (
                <div className="space-y-4">
                  {createdHOAData.board_member_credentials.map((credential, index) => (
                    <div key={index} className="text-left space-y-2 bg-white p-4 rounded-lg border">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-blue-800">{credential.title}</h4>
                        <span className="text-xs bg-blue-100 px-2 py-1 rounded text-blue-700">{credential.name}</span>
                      </div>
                      <div>
                        <p className="text-sm text-blue-600">Username (Email):</p>
                        <p className="font-mono bg-blue-100 px-3 py-2 rounded text-blue-800 break-all text-sm">
                          {credential.email}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-600">Password:</p>
                        <p className="font-mono bg-blue-100 px-3 py-2 rounded text-blue-800 text-sm">
                          {credential.password}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  <div className="mt-3 p-3 bg-blue-100 rounded-lg">
                    <p className="text-xs text-blue-700">
                      💡 <strong>Save these credentials!</strong> You'll need them to login next time. 
                      A confirmation email will be sent to each board member's email address.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-left space-y-2">
                  <div>
                    <p className="text-sm text-blue-600">Primary Contact Email:</p>
                    <p className="font-mono bg-blue-100 px-3 py-2 rounded text-blue-800 break-all">
                      {hoaData.contact_email}
                    </p>
                  </div>
                  <div className="mt-3 p-3 bg-blue-100 rounded-lg">
                    <p className="text-xs text-blue-700">
                      📧 Check your email for login instructions. If no board member email was provided during setup, 
                      please contact support to set up your admin account.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
              <h4 className="font-medium text-blue-800 mb-2">Next Steps:</h4>
              <ol className="text-sm text-blue-700 text-left space-y-1">
                <li>1. Board members will receive email invitations</li>
                <li>2. Upload your HOA documents and rules</li>
                <li>3. Configure payment settings for dues collection</li>
                <li>4. Invite homeowners to join the portal</li>
                <li>5. Schedule your first board meeting</li>
              </ol>
            </div>
            
            <div className="space-y-4">
              <button
                onClick={() => setShowOnboarding(true)}
                className="w-full max-w-sm bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold"
              >
                Start Setup Checklist
              </button>
              
              <button
                onClick={() => onComplete('admin')}
                className="w-full max-w-sm border border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-6 py-3 rounded-lg font-semibold"
              >
                Skip to Dashboard
              </button>
              
              <button
                onClick={onBack}
                className="w-full max-w-sm border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-lg font-semibold"
              >
                Back to Home
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={onBack}
            className="inline-flex items-center text-indigo-600 hover:text-indigo-500 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900">Set Up Your HOA Community</h1>
          
          {/* Progress bar */}
          {currentStep < 10 && (
            <div className="mt-6">
              <div className="flex items-center justify-center space-x-1">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                      currentStep >= step 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {step}
                    </div>
                    {step < 9 && (
                      <div className={`w-3 h-1 ${
                        currentStep > step ? 'bg-indigo-600' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-center space-x-2 mt-2 text-xs text-gray-600">
                <span className="w-12 text-center">Basic</span>
                <span className="w-12 text-center">Community</span>
                <span className="w-12 text-center">Board</span>
                <span className="w-12 text-center">Legal</span>
                <span className="w-12 text-center">Plan</span>
                <span className="w-12 text-center">Auth</span>
                <span className="w-12 text-center">Payment</span>
                <span className="w-12 text-center">Review</span>
                <span className="w-12 text-center">Done</span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {renderStep()}
          
          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}
          
          {/* Navigation buttons */}
          {currentStep < 10 && (
            <div className="flex justify-between mt-8">
              <button
                onClick={currentStep === 1 ? onBack : prevStep}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                {currentStep === 1 ? 'Cancel' : 'Previous'}
              </button>
              
              {/* Save Quote Button - Available from step 5 onwards, before authorization step */}
              {currentStep >= 5 && currentStep < 6 && hoaData.contact_email && (
                <button
                  onClick={saveQuote}
                  disabled={loading}
                  className="px-6 py-2 border-2 border-green-600 text-green-600 rounded-md hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  💾 Save Quote
                </button>
              )}
              
              <button
                onClick={currentStep === 7 ? handleSubmit : nextStep}
                disabled={!validateStep(currentStep) || loading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating HOA...
                  </div>
                ) : currentStep === 7 ? (
                  appliedCoupon && (appliedCoupon.type === 'free' || appliedCoupon.type === 'delayed_billing')
                    ? 'Create My HOA' 
                    : 'Proceed to Payment'
                ) : 'Next'}
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Save Quote Success Modal */}
      {savedQuoteCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-center mb-2">Quote Saved Successfully! 💾</h3>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-800 mb-2">
                <strong>Your unique quote code:</strong>
              </p>
              <div className="bg-white border border-green-300 rounded px-3 py-2 font-mono text-lg font-bold text-green-700 text-center">
                {savedQuoteCode}
              </div>
              <p className="text-xs text-green-600 mt-2 text-center">
                Valid for 30 days • Keep this code safe!
              </p>
            </div>
            
            <p className="text-sm text-gray-600 mb-4 text-center">
              Use this code to continue your setup anytime. We've saved all your progress including your plan selection and pricing.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(savedQuoteCode);
                  alert('Quote code copied to clipboard!');
                }}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium"
              >
                📋 Copy Code
              </button>
              <button
                onClick={() => {
                  // Improved print function that prevents navigation issues
                  // Get plan details
                  const plans = {
                    basic: { name: 'Basic', price: 299, homes: 100, storage: 10, tier: 0, features: ['Document Management', 'Member Portal', 'Basic Communications', 'Payment Tracking', 'Meeting Minutes'] },
                    standard: { name: 'Standard', price: 499, homes: 300, storage: 25, tier: 1, features: ['All Basic Features', 'Advanced Reporting', 'Email Campaigns', 'Violation Tracking', 'Budget Tools', 'Calendar Management'] },
                    premium: { name: 'Premium', price: 1020, homes: 500, storage: 100, tier: 2, features: ['All Standard Features', 'Priority Support', 'Custom Branding', 'API Access', 'Dedicated Account Manager', 'Advanced Analytics'] }
                  };
                  
                  const selectedPlanData = plans[selectedPlan] || plans.basic;
                  const previousTier = selectedPlanData.tier > 0 ? Object.values(plans).find(p => p.tier === selectedPlanData.tier - 1) : null;
                  
                  // Calculate next billing date (1 year from today)
                  const nextBillingDate = new Date();
                  nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
                  
                  // Build add-ons list with checkboxes
                  const allAddons = [
                    { id: 'voting_platform', name: 'Voting Platform', price: 100 },
                    { id: 'amenity_management', name: 'Amenity Management', price: 100 },
                    { id: 'budget_finance', name: 'Budget & Finance', price: 100 },
                    { id: 'custom_pages', name: 'Custom Pages', price: 150 }
                  ];
                  
                  const addonsHTML = allAddons.map(addon => {
                    const isSelected = selectedAddOns.includes(addon.id);
                    return `<div style="padding: 8px 0; display: flex; align-items: center;">
                      <span style="font-size: 18px; margin-right: 10px;">${isSelected ? '☑' : '☐'}</span>
                      <span style="flex: 1;">${addon.name}</span>
                      <span style="font-weight: bold;">$${addon.price}/yr</span>
                    </div>`;
                  }).join('');
                  
                  // Check for discount
                  const hasDiscount = appliedCoupon && (appliedCoupon.code || appliedCoupon.type);
                  let discountHTML = '';
                  if (hasDiscount) {
                    if (appliedCoupon.type === 'percentage') {
                      discountHTML = `<div style="background: #fff3cd; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
                        <p style="margin: 0; color: #856404;"><strong>🎉 Discount Applied:</strong> ${appliedCoupon.discount}% off</p>
                      </div>`;
                    } else if (appliedCoupon.type === 'delayed_billing') {
                      discountHTML = `<div style="background: #d4edda; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
                        <p style="margin: 0; color: #155724;"><strong>🎁 Trial Applied:</strong> ${appliedCoupon.delay_months} month${appliedCoupon.delay_months > 1 ? 's' : ''} free trial</p>
                      </div>`;
                    }
                  }
                  
                  const printContent = `
                    <div style="font-family: Arial, sans-serif; padding: 30px; max-width: 800px; margin: 0 auto;">
                      <div style="text-align: center; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px;">
                        <h1 style="margin: 0; color: #1e40af; font-size: 32px;">HOA Manager Pro</h1>
                        <h2 style="margin: 10px 0; color: #64748b; font-size: 20px;">Professional Setup Quote</h2>
                        <p style="margin: 5px 0; font-size: 14px; color: #94a3b8;">Generated on ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                      
                      ${discountHTML}
                      
                      <div style="background: #f8fafc; padding: 25px; border-radius: 8px; margin-bottom: 25px; border: 1px solid #e2e8f0;">
                        <h3 style="margin-top: 0; color: #1e40af; border-bottom: 2px solid #3b82f6; padding-bottom: 10px;">Quote Information</h3>
                        <table style="width: 100%; border-collapse: collapse;">
                          <tr><td style="padding: 8px 0; width: 40%;"><strong>Quote Code:</strong></td><td style="font-family: monospace; font-size: 16px; color: #2563eb;">${savedQuoteCode}</td></tr>
                          <tr><td style="padding: 8px 0;"><strong>HOA Name:</strong></td><td>${hoaData.name || 'Not specified'}</td></tr>
                          <tr><td style="padding: 8px 0;"><strong>Contact Email:</strong></td><td>${hoaData.contact_email}</td></tr>
                          <tr><td style="padding: 8px 0;"><strong>Address:</strong></td><td>${hoaData.address || 'Not specified'}, ${hoaData.city || ''}, ${hoaData.state || ''} ${hoaData.zip_code || ''}</td></tr>
                          <tr><td style="padding: 8px 0;"><strong>Properties:</strong></td><td>${hoaData.total_properties || 'Not specified'}</td></tr>
                        </table>
                      </div>
                      
                      <div style="background: #eff6ff; padding: 25px; border-radius: 8px; margin-bottom: 25px; border: 2px solid #3b82f6;">
                        <h3 style="margin-top: 0; color: #1e40af;">Selected Plan: ${selectedPlanData.name}</h3>
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
                          <div>
                            <p style="margin: 5px 0; font-size: 14px;">Up to ${selectedPlanData.homes} homes • ${selectedPlanData.storage}GB storage</p>
                            <p style="margin: 5px 0; font-size: 24px; font-weight: bold; color: #1e40af;">$${selectedPlanData.price}/year</p>
                          </div>
                        </div>
                        <p style="margin: 10px 0; padding: 10px; background: white; border-radius: 5px;"><strong>Next Billing Date (if signed up today):</strong> ${nextBillingDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                      
                      <div style="background: #f1f5f9; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
                        <h3 style="margin-top: 0; color: #334155;">✨ Included Features</h3>
                        <div style="column-count: 2; column-gap: 20px;">
                          ${selectedPlanData.features.map(f => `<div style="padding: 5px 0; break-inside: avoid;">✓ ${f}</div>`).join('')}
                        </div>
                        ${previousTier ? `
                          <h4 style="margin: 20px 0 10px 0; color: #64748b; font-size: 14px;">Plus all features from ${previousTier.name}:</h4>
                          <div style="column-count: 2; column-gap: 20px; font-size: 13px; color: #64748b;">
                            ${previousTier.features.map(f => `<div style="padding: 3px 0; break-inside: avoid;">✓ ${f}</div>`).join('')}
                          </div>
                        ` : ''}
                      </div>
                      
                      <div style="background: white; padding: 20px; border-radius: 8px; margin-bottom: 20px; border: 1px solid #e2e8f0;">
                        <h3 style="margin-top: 0; color: #334155;">📦 Available Add-ons</h3>
                        ${addonsHTML}
                      </div>
                      
                      <div style="background: #f0fdf4; padding: 20px; border-radius: 8px; margin-bottom: 20px; border: 2px solid #22c55e;">
                        <h3 style="margin-top: 0; color: #15803d;">💰 Total Investment</h3>
                        <p style="font-size: 32px; font-weight: bold; margin: 10px 0; color: #15803d;">$${calculateTotalPrice()}/year</p>
                        <p style="font-size: 14px; color: #16a34a; margin: 5px 0;">Quote valid until: ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                      
                      <div style="margin-bottom: 20px; padding: 20px; background: #fefce8; border-radius: 8px; border: 1px solid #fde047;">
                        <h3 style="margin-top: 0; color: #854d0e;">📋 Next Steps</h3>
                        <ol style="line-height: 1.8; color: #713f12; margin: 0; padding-left: 20px;">
                          <li>Present this quote to your HOA board for approval</li>
                          <li>Visit <strong>hoamanagerpro.us</strong> and click "Load Saved Quote"</li>
                          <li>Enter your quote code: <strong style="background: white; padding: 2px 8px; border-radius: 3px; font-family: monospace;">${savedQuoteCode}</strong></li>
                          <li>Complete your setup and payment to activate your account</li>
                        </ol>
                      </div>
                      
                      <div style="border-top: 2px solid #e5e7eb; padding-top: 20px; font-size: 12px; color: #6b7280; text-align: center;">
                        <p style="margin: 5px 0;">Questions? Contact us at <strong>support@hoamanagerpro.us</strong></p>
                        <p style="margin: 5px 0;">This quote expires 30 days from the generation date.</p>
                        <p style="margin: 5px 0; color: #9ca3af;">© ${new Date().getFullYear()} HOA Manager Pro. All rights reserved.</p>
                      </div>
                    </div>
                  `;
                  
                  // Use safer print method to prevent navigation issues
                  try {
                    // Create new window with proper cleanup
                    const printWindow = window.open('', '_blank', 'width=800,height=600');
                    if (printWindow) {
                      printWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                          <head>
                            <title>HOA Quote ${savedQuoteCode}</title>
                            <style>
                              body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                              @media print { body { padding: 10px; } }
                            </style>
                          </head>
                          <body>
                            ${printContent}
                            <script>
                              // Auto-print when ready
                              window.onload = function() {
                                setTimeout(function() {
                                  window.print();
                                }, 1000);
                              };
                              
                              // Handle print events to close window
                              window.onafterprint = function() {
                                setTimeout(function() {
                                  window.close();
                                }, 1000);
                              };
                            </script>
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                    } else {
                      // Fallback: copy to clipboard and alert
                      navigator.clipboard.writeText(savedQuoteCode).then(() => {
                        alert(`Quote saved! Code: ${savedQuoteCode}\n\n(Code copied to clipboard)\n\nUse this code to continue your setup later.`);
                      }).catch(() => {
                        alert(`Quote saved! Please save this code: ${savedQuoteCode}\n\nUse this code to continue your setup later.`);
                      });
                    }
                  } catch (error) {
                    console.error('Print failed:', error);
                    // Safe fallback
                    alert(`Quote saved successfully!\n\nQuote Code: ${savedQuoteCode}\n\nPlease write down this code to continue your setup later.`);
                  }
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium"
              >
                🖨️ Print Quote
              </button>
            </div>
            <div className="mt-2">
              <button
                onClick={() => setSavedQuoteCode('')}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm font-medium"
              >
                Continue Setup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedHOASetupFlow;